import { memo, useState, useEffect, useContext } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Box,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
  Paper,
  Button
} from '@mui/material';
import {
  FavoriteBorder,
  Favorite,
  Chat,
  Call,
  LocationOn,
  ChevronLeft,
  ChevronRight,
  Explore,
  CropSquare,
  AccountCircle
} from '@mui/icons-material';
import { getPropertyImages } from './api.js';
import { AuthContext } from './../../AuthContext/AuthContext';
import { BASE_URL } from './../../Api/ApiUrls';
import GuestLoginModal from '../../GuestLoginModal';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const formatPrice = (price) => {
  const num = Number(price);
  if (num >= 10000000) {
    return `${(num / 10000000).toFixed(2)} Cr`;
  } else if (num >= 100000) {
    return `${(num / 100000).toFixed(2)} L`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(2)} K`;
  }
  return num.toLocaleString('en-IN');
};

function PropertyCardImpl({ property, selected, onClick, onView, onWishlistToggle = () => false, isSaved: isSavedProp,
   toggleSave: toggleSaveProp , showAlert, activityType  }) {
  const { userId, isGuest } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [featureRequested, setFeatureRequested] = useState('');
  const [userPlan, setUserPlan] = useState(null);
  const [userActivities, setUserActivities] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [userType] = useState('Buyer');  

  useEffect(() => {
    const fetchUserPlan = async () => {
      if (!userId || !userType) {
        setUserPlan(null);
        return;
      }
      try {
        const response = await axios.get(`${BASE_URL}/Get-user-plan/${userId}/`);
        const plans = response.data;
        const activePlan = Array.isArray(plans)
          ? plans.find(plan => plan.user_type === userType)
          : plans.user_type === userType ? plans : null;
        setUserPlan(activePlan);
      } catch (error) {
        console.error('Error fetching user plan:', error);
        setUserPlan(null);
      }
    };
    fetchUserPlan();
  }, [userId, userType]);

  // Fetch user activities
  useEffect(() => {
    const fetchUserActivities = async () => {
      if (!userId) return;
      try {
        const response = await fetch(`${BASE_URL}/activities/`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const allActivities = await response.json();
        const filteredActivities = allActivities.filter(activity =>
          activity && activity.user_id && String(activity.user_id) === String(userId)
        );
        setUserActivities(filteredActivities);
      } catch (error) {
        console.error('Error fetching activities:', error);
        setUserActivities([]);
      }
    };
    fetchUserActivities();
  }, [userId]);

  // Load wishlist
  useEffect(() => {
    const loadWishlist = async () => {
      if (!userId) return;
      try {
        const response = await fetch(`${BASE_URL}/get-user-cart/${userId}/`);
        const data = await response.json();
        setWishlistItems(data);
      } catch (error) {
        console.error('Error loading wishlist:', error);
      }
    };
    loadWishlist();
  }, [userId]);

  // Fetch images
  useEffect(() => {
    const fetchImages = async () => {
      if (!property || !property.id) {
        setLoadingImages(false);
        return;
      }
      
      setLoadingImages(true);
      try {
        const response = await getPropertyImages(property.id);
        console.log("Images API Response:", response);
        if (response.success && response.images?.length > 0) {
          const firstImage = response.images[0];
          const imageUrl = firstImage.startsWith('http')
            ? firstImage
            : `${BASE_URL}/${firstImage.replace(/^\/+/, '')}`;
          setImages([imageUrl]);
        } else {
          setImages(['/default-property-image.jpg']);
        }
      } catch (error) {
        console.error(`Error fetching images for property ${property.id}:`, error);
        setImages(['/default-property-image.jpg']);
      } finally {
        setLoadingImages(false);
      }
    };
    
    fetchImages();
  }, [property.id]);

  const displayImages = images.length > 0 ? images : ['/default-property-image.jpg'];

  // Helper: Check if user is guest and show modal
  const checkGuestAndShowModal = (actionCallback) => {
    if (isGuest) {
      setShowGuestModal(true);
      return false;
    }
    return actionCallback();
  };

  // Check property limit
  const checkPropertyLimit = (currentPropertyId) => {
    if (userPlan?.no_of_properties_unlimited) return true;
    
    const alreadyAccessed = userActivities.some(
      activity => activity.property_id === currentPropertyId && activity.activity_as === userType
    );
    if (alreadyAccessed) return true;
    
    const relevantActivities = userActivities.filter(activity => activity.activity_as === userType);
    const uniqueProperties = new Set(relevantActivities.map(a => a.property_id));
    const propertyLimit = userPlan?.no_of_properties || 10;
    
    if (uniqueProperties.size >= propertyLimit) {
      setShowLimitModal(true);
      return false;
    }
    return true;
  };

  // Log activity to backend
  const logActivity = async (activityType) => {
    try {
      const existingActivity = userActivities.find(
        activity => activity.property_id === property.id && 
                    activity.activity_as === userType
      );

      if (existingActivity) {
        console.log('Activity already exists for this property');
        return true;
      }

      let propertyOwner = property.owner_id ?? property.user_id ?? null;

      if (!propertyOwner) {
        try {
          const propertyResponse = await fetch(`${BASE_URL}/property/${property.id}/`);
          if (propertyResponse.ok) {
            const propertyData = await propertyResponse.json();
            propertyOwner = propertyData.user_id ?? propertyData.owner ?? propertyData.posted_by ?? null;
          }
        } catch (fetchErr) {
          console.error('Failed to fetch property owner:', fetchErr);
        }
      }

      const activityData = {
        user_id: userId,
        property_id: property.id,
        activity_type: activityType,
        property_by: propertyOwner,
        activity_as: userType || "Buyer"  
      };

      console.log("Sending Activity Data:", activityData);

      const response = await fetch(`${BASE_URL}/activities/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(activityData)
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.log("Backend Error:", errorData);
        throw new Error('Failed to log activity');
      }

      const newActivity = await response.json();
      setUserActivities(prev => [...prev, newActivity]);
      return true;
    } catch (err) {
      console.error("Error logging activity:", err);
      return false;
    }
  };

  // Handle any action with subscription and limit checks
  const handleAction = async (e, actionName, actionCallback, activityType) => {
    e.stopPropagation();

    if (isGuest) {
      setShowGuestModal(true);
      return;
    }

    const alreadyAccessed = userActivities.some(
      activity => activity.property_id === property.id && activity.activity_as === userType
    );

    if (!alreadyAccessed) {
      const withinLimit = checkPropertyLimit(property.id);
      if (!withinLimit) return;
    }

    try {
      await actionCallback();
      if (activityType && !alreadyAccessed) await logActivity(activityType);
    } catch (error) {
      console.error(`Error in ${actionName} action:`, error);
    }
  };

  // Handle location click
  const handleLocationClick = (e) => {
    handleAction(e, "Location", async () => {
      setLoading(true);

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      const openInMaps = (url) => {
        setLoading(false);
        window.location.href = url;
      };

      const fallbackUrl = isIOS
        ? `http://maps.apple.com/?q=${encodeURIComponent(property.location || '')}`
        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.location || `${property.lat},${property.lng}`)}`;

      if (property.lat && property.lng && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLat = position.coords.latitude;
            const userLng = position.coords.longitude;
            const mapsUrl = isIOS
              ? `http://maps.apple.com/?daddr=${property.lat},${property.lng}`
              : `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${property.lat},${property.lng}&travelmode=driving`;
            openInMaps(mapsUrl);
          },
          (error) => {
            console.error("Error getting user location:", error);
            openInMaps(fallbackUrl);
          },
          { timeout: 10000 }
        );
      } else {
        openInMaps(fallbackUrl);
      }
    }, "Location");
  };

  // Handle message click
  const handleMessageClick = (e) => {
    handleAction(e, "Message", async () => {
      navigate('/chat', { state: { property: property.id } });
    }, "Message");
  };

  // Handle call click
  const handleCallClick = (e) => {
    handleAction(e, "Call", async () => {
      if (property.mobileNo) {
        try {
          await navigator.clipboard.writeText(property.mobileNo);
          console.log(`Phone number copied: ${property.mobileNo}`);
          if (showAlert) {
            showAlert('success', `Phone number copied: ${property.mobileNo}`);
          }
        } catch (error) {
          console.error('Failed to copy:', error);
          if (showAlert) {
            showAlert('failure', 'Failed to copy phone number');
          }
        }
      } else {
        console.log('No phone number available');
        if (showAlert) {
          showAlert('failure', 'No phone number available');
        }
      }
    }, "Call");
  };

const handleWishlistClick = async (e) => {
  e.stopPropagation();

  if (isGuest) {
    setShowGuestModal(true);
    return;
  }

  if (wishlistLoading) return;

  const isSaved = wishlistItems.some(item => item.property_id === property.id);
  
  if (isSaved) {
    setWishlistItems(prev => prev.filter(item => item.property_id !== property.id));
  } else {
    setWishlistItems(prev => [...prev, {
      cart_id: wishlistItems.length + 1,
      property_id: property.id,
      user_id: userId
    }]);
  }
  
  setWishlistLoading(true);
  
  try {
    if (isSaved) {
      // Remove from wishlist
      const cartItem = wishlistItems.find(item => item.property_id === property.id);
      if (cartItem && cartItem.cart_id) {
        await fetch(`${BASE_URL}/user-cart/${cartItem.cart_id}/`, {
          method: 'DELETE',
        });
      }
      
      if (onWishlistToggle) {
        onWishlistToggle(property);
      }
    } else {
      // Add to wishlist
      const response = await fetch(`${BASE_URL}/user-cart/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: userId, 
          property_id: property.id 
        }),
      });
      const data = await response.json();
      
      // Update with real cart_id from server
      if (response.ok && data && data.cart_id) {
        setWishlistItems(prev => 
          prev.map(item => 
            item.property_id === property.id 
              ? { ...item, cart_id: data.cart_id }
              : item
          )
        );
      }
      
      if (onWishlistToggle) {
        onWishlistToggle(property);
      }
      
      // Log activity for wishlist
      await logActivity("Liked");
    }
  } catch (error) {
    console.error('Wishlist error:', error);
    // Rollback on error
    if (isSaved) {
      // If remove failed, add back
      const originalItem = wishlistItems.find(item => item.property_id === property.id);
      if (originalItem) {
        setWishlistItems(prev => [...prev, originalItem]);
      }
    } else {
      // If add failed, remove the optimistic item
      setWishlistItems(prev => prev.filter(item => item.property_id !== property.id));
    }
  } finally {
    setWishlistLoading(false);
  }
};

  // Handle card click for viewing details
  const handleCardClick = async (e) => {
    const isButtonClick = e.target.closest('button') || e.target.closest('svg');
    if (!isButtonClick && onClick) {
      if (isGuest) {
        setShowGuestModal(true);
        return;
      }

      const alreadyAccessed = userActivities.some(
        activity =>
          activity.property_id === property.id &&
          activity.activity_as === userType &&
          activity.activity_type === activityType
      );
      
      if (!alreadyAccessed) {
        const withinLimit = checkPropertyLimit(property.id);
        if (!withinLimit) return;
      }

      if (!alreadyAccessed) {
        try {
          await logActivity("Description");
        } catch (error) {
          console.error('Error logging description view activity:', error);
        }
      }
localStorage.setItem('lastViewedPropertyId', property.id);

      navigate(`/hot-property-description/${property.id}`);
    }
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % displayImages.length);
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + displayImages.length) % displayImages.length);
  };

  // Determine if property is saved from wishlistItems state
  const isPropertySaved = wishlistItems.some(item => item.property_id === property.id);

  return (
    <>
      <Card
        onClick={handleCardClick}
        sx={{
          display: "flex",
          flexDirection: "row",
          width: '100%',
          height: 135,
          maxWidth: 290,
          cursor: 'pointer',
          borderRadius: 2,
          overflow: 'hidden',
          border: selected ? '2px solid #FFD600' : '2px solid transparent',
          transform: selected ? 'translateY(-2px)' : 'none',
          transition: 'transform 200ms, border-color 200ms',
          boxShadow: selected ? '0 4px 12px rgba(0,0,0,0.5)' : '0 2px 6px rgba(0,0,0,0.3)',
          bgcolor: '#fff',
          flexShrink: 0,
        }}
      >
        {/* Left Side: Image with Carousel */}
        <Box
          sx={{
            position: "relative",
            width: "38%",
            height: "90%",
            p: 0.8,
            pr: 0
          }}
        >
          {loadingImages ? (
            <Box sx={{ 
              width: "100%", 
              height: "100%", 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: '#f5f5f5',
              borderRadius: 2
            }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <CardMedia
              component="img"
              image={displayImages[currentImageIndex]}
              alt="Property"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: 2,
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
              }}
            />
          )}

          {/* Carousel Navigation Arrows */}
          {displayImages.length > 1 && !loadingImages && (
            <>
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: 4,
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255,255,255,0.5)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.8)" },
                  p: 0.3,
                  width: 22,
                  height: 22
                }}
                onClick={handlePrevImage}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 4,
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255,255,255,0.5)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.8)" },
                  p: 0.3,
                  width: 22,
                  height: 22
                }}
                onClick={handleNextImage}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </>
          )}

          {/* Carousel Indicators */}
          {displayImages.length > 1 && !loadingImages && (
            <Box
              sx={{
                position: "absolute",
                bottom: 6,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: "4px",
              }}
            >
              {displayImages.map((_, index) => (
                <Box
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: currentImageIndex === index ? "#1976d2" : "#ccc",
                    cursor: "pointer",
                  }}
                />
              ))}
            </Box>
          )}

          {/* Wishlist Icon */}
          <Tooltip title={isPropertySaved ? "Remove from Wishlist" : "Add to Wishlist"}>
            <IconButton
              sx={{
                position: "absolute",
                top: 7,
                right: 3,
                bgcolor: "white",
                boxShadow: 1,
                p: 0.5,
                width: 24,
                height: 24,
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
              onClick={handleWishlistClick}
              disabled={wishlistLoading}
            >
              {wishlistLoading ? (
                <CircularProgress size={16} />
              ) : isPropertySaved ? (
                <Favorite color="error" fontSize="small" />
              ) : (
                <FavoriteBorder fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        <CardContent
          sx={{
            width: "62%",
            p: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: 135,
            boxSizing: 'border-box'
          }}
        >
          {/* Title + Price */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography
              fontWeight="bold"
              sx={{
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "60%",
                lineHeight: 1.2
              }}
            >
               {property.title || `PROP${property.id}`}
            </Typography>

            <Typography
              fontWeight="bold"
              color="primary"
              sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
            >
              ₹{formatPrice(property.price)}
            </Typography>
          </Box>

          {/* Icons and Date */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 0.5,
              mb: 0.5,
            }}
          >
            {/* Icons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title="View directions in Google Maps">
                <IconButton
                  size="small"
                  onClick={handleLocationClick}
                  sx={{ p: 0.3, color: "primary.main" }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <LocationOn sx={{ fontSize: "0.9rem" }} />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip title="Send Message">
                <IconButton
                  size="small"
                  onClick={handleMessageClick}
                  sx={{ p: 0.3, color: "primary.main" }}
                >
                  <Chat sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Call">
                <IconButton
                  size="small"
                  onClick={handleCallClick}
                  sx={{ p: 0.3, color: "success.main" }}
                >
                  <Call sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Date */}
            <Typography color="text.secondary" sx={{ fontSize: "0.65rem" }}>
              {property.createdAt
                ? new Date(property.createdAt).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : "N/A"}
            </Typography>
          </Box>

          {/* Property Info Grid */}
          <Box
            sx={{
              display: "flex",
              border: "1px solid #e0e0e0",
              borderRadius: "6px",
              overflow: "hidden",
              minHeight: 50,
            }}
          >
            {/* Facing */}
            <Box
              sx={{
                flex: 1.2,
                mt: 0.2,
                minWidth: 0,
                textAlign: "center",
                p: 0.5,
                borderRight: "1px solid #e0e0e0",
              }}
            >
              <Explore sx={{ fontSize: "0.9rem", color: "primary.main" }} />

              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: "0.55rem",
                  lineHeight: 1.1
                }}
              >
                {property.facing ? property.facing.split("-")[0] : "N/A"}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.5rem",
                  lineHeight: 1.1,
                  color: '#666'
                }}
              >
                {property.facing?.split("-")[1] || ""}
              </Typography>
            </Box>

            {/* Area */}
            <Box
              sx={{
                flex: 1.4,
                mt: 0.2,
                minWidth: 0,
                textAlign: "center",
                p: 0.5,
                borderRight: "1px solid #e0e0e0",
              }}
            >
              <CropSquare sx={{ fontSize: "0.9rem", color: "primary.main" }} />

              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: "0.55rem",
                  lineHeight: 1.1,
                  maxWidth: "60px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                {property.siteArea || property.buildupArea || property.area || "N/A"}
              </Typography>

              {property.units && (
                <Typography
                  sx={{
                    fontSize: "0.5rem",
                    lineHeight: 1.1,
                    color: "#666"
                  }}
                >
                  {property.units}
                </Typography>
              )}
            </Box>

            {/* Posted By */}
            <Box
              sx={{
                flex: 1,
                mt: 0.2,
                minWidth: 0,
                textAlign: "center",
                p: 0.3,
              }}
            >
              <AccountCircle sx={{ fontSize: "0.9rem", color: "primary.main" }} />

              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: "0.55rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.1
                }}
              >
                {property.postedBy || "N/A"}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Subscribe Modal */}
      {showSubscribeModal && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Paper sx={{ padding: 2, maxWidth: 250, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Subscription Required</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Please subscribe to use the {featureRequested} feature.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setShowSubscribeModal(false)}
                size="small"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setShowSubscribeModal(false);
                  navigate('/user-subscription');
                }}
                size="small"
              >
                Subscribe
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

  

      {/* Guest Login Modal */}
      <GuestLoginModal
        open={showGuestModal}
        onClose={() => {
          setShowGuestModal(false);
        }}
      />
    </>
  );
}

export default memo(PropertyCardImpl, (prevProps, nextProps) => {
  return prevProps.property?.id === nextProps.property?.id && 
         prevProps.selected === nextProps.selected;
});